import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { isBrowser } from "@cinch-labs/shared-util";
import { useUserStore } from "@cinch-nx/data-user";
import { Env, readFromEnv } from "@cinch-nx/environments";
import { initDataDogLogging, initDataDogRUM } from "@cinch-nx/shared-datadog";
import { useRouter } from "next/router";
import Script from "next/script";
import React, { useEffect, useState } from "react";
import { updateVehicleInformation } from "../data-layer";
import { DirectCallRules } from "../direct-call-rules";
import { getStandardPageInfo, updatePageInfo } from "../event-page-view";
import { resetProductDisplay } from "../product-display";
import { segmentSnippet } from "../segment/initialisation-script";
import { SimpleAnalyticsScripts } from "../simple-analytics/script-loader";
import { TrackingEventTypes } from "./types";
import { getCookieConsentPreferences, useAnalytics } from "./use-analytics.hook";
var TIKTOK_CLICKID_QUERY_PARAMETER = "ttclid";
var isPerformanceNavigationTiming = function(input) {
    return (input === null || input === void 0 ? void 0 : input.entryType) === "navigation";
};
var useTrackNextRouterEvents = function(trackEvent, pathsWithCustomAdobePageViewEventImplementations) {
    var router = useRouter();
    var handleRouteChange = function() {
        var ref;
        var prevLocation = JSON.parse((ref = sessionStorage.getItem("prevLocation")) !== null && ref !== void 0 ? ref : "{}");
        if ((prevLocation === null || prevLocation === void 0 ? void 0 : prevLocation.pathname) === undefined) {
            prevLocation = undefined;
        }
        // eslint-disable-next-line no-restricted-globals
        updatePageInfo({
            location: location,
            prevLocation: prevLocation
        });
        updateVehicleInformation({});
        var eventName = prevLocation ? DirectCallRules.NAME_PAGE_VIEW : DirectCallRules.NAME_INITIAL_PAGE_LOAD;
        var isPageView = eventName === DirectCallRules.NAME_PAGE_VIEW;
        if (isPageView) {
            resetProductDisplay();
        }
        if (isBrowser) {
            var ref1, ref2, ref3;
            var eventType = (pathsWithCustomAdobePageViewEventImplementations === null || pathsWithCustomAdobePageViewEventImplementations === void 0 ? void 0 : pathsWithCustomAdobePageViewEventImplementations.includes(window.location.pathname)) ? TrackingEventTypes.PAGE_NO_ADOBE : TrackingEventTypes.PAGE;
            trackEvent({
                type: eventType,
                eventName: (ref1 = window.adobeData) === null || ref1 === void 0 ? void 0 : (ref2 = ref1.page) === null || ref2 === void 0 ? void 0 : (ref3 = ref2.pageInfo) === null || ref3 === void 0 ? void 0 : ref3.pageName,
                data: getStandardPageInfo(window.location.pathname, prevLocation === null || prevLocation === void 0 ? void 0 : prevLocation.pathname)
            });
            var params = new URLSearchParams(window.location.search);
            var ttclid = params.get(TIKTOK_CLICKID_QUERY_PARAMETER);
            if (ttclid) {
                trackEvent({
                    type: TrackingEventTypes.IDENTIFY,
                    data: {
                        ttclid: ttclid
                    }
                });
            }
        }
    };
    // eslint-disable-next-line unicorn/consistent-function-scoping
    var onRouteChangeStart = function() {
        if (isBrowser) {
            sessionStorage.setItem("prevLocation", JSON.stringify(window.location));
        }
    };
    var onRouteChangeComplete = function(_url, param) {
        var shallow = param.shallow;
        if (shallow) return;
        handleRouteChange();
    };
    var onCachedPageShow = function(event) {
        var navigation = performance.getEntriesByType("navigation")[0];
        if (isPerformanceNavigationTiming(navigation)) {
            var navigationType = navigation.type;
            if (event.persisted && navigationType === "back_forward") {
                handleRouteChange();
            }
        }
    };
    useEffect(function() {
        if (!router) {
            return;
        }
        handleRouteChange();
        router.events.on("routeChangeStart", onRouteChangeStart);
        router.events.on("routeChangeComplete", onRouteChangeComplete);
        window.addEventListener("pageshow", onCachedPageShow);
        return function() {
            router.events.off("routeChangeStart", onRouteChangeStart);
            router.events.off("routeChangeComplete", onRouteChangeComplete);
            window.removeEventListener("pageshow", onCachedPageShow);
        };
    }, []);
};
var useDatadog = function() {
    var ref = useState(false), hasInitialised = ref[0], setHasInitialised = ref[1];
    var getSampleRate = function() {
        var fromEnv = Number.parseInt(readFromEnv(Env.DataDogRumGlobalSampleRate), 10);
        return fromEnv && !Number.isNaN(fromEnv) ? fromEnv : 50;
    };
    var datadogEnvironment = {
        appVersion: readFromEnv(Env.BuildId),
        applicationId: readFromEnv(Env.DataDogRumApplicationId),
        dataDogClientId: readFromEnv(Env.DataDogClientId),
        datadogRumClientToken: readFromEnv(Env.DataDogRumClientToken),
        rumGlobalSampleRate: getSampleRate(),
        serviceName: readFromEnv(Env.DataDogServiceName)
    };
    useEffect(function() {
        if (!hasInitialised) {
            setHasInitialised(true);
            initDataDogLogging(datadogEnvironment);
            initDataDogRUM(datadogEnvironment);
        }
    }, [
        hasInitialised
    ]);
};
export var AnalyticsManager = function(param) {
    var pathsWithCustomAdobePageViewEventImplementations = param.pathsWithCustomAdobePageViewEventImplementations;
    var ref = useUserStore(), userStatus = ref.status, token = ref.token;
    var ref1 = useAnalytics(), eventsQueue = ref1.eventsQueue, processEventsQueue = ref1.processEventsQueue, hasAcceptedCookies = ref1.hasAcceptedCookies, hasLoadedScripts = ref1.hasLoadedScripts, trackEvent = ref1.trackEvent;
    useDatadog();
    useTrackNextRouterEvents(trackEvent, pathsWithCustomAdobePageViewEventImplementations);
    useEffect(function() {
        processEventsQueue();
    }, [
        eventsQueue,
        processEventsQueue,
        hasAcceptedCookies,
        hasLoadedScripts
    ]);
    useEffect(function() {
        if (userStatus === "valid") {
            trackEvent({
                type: TrackingEventTypes.IDENTIFY,
                userId: token === null || token === void 0 ? void 0 : token.profile["https://cinch.co.uk/user_id"]
            });
        }
    }, [
        token === null || token === void 0 ? void 0 : token.profile.sub,
        trackEvent,
        userStatus
    ]);
    var analyticsScripts = "https://assets.adobedtm.com/".concat(readFromEnv(Env.Adobe), ".js");
    var marketingScripts = [
        "https://www.googletagmanager.com/gtag/js?id=DC-10250799",
        "https://www.googletagmanager.com/gtag/js?id=G-P06Z9MC4S",
        "https://p.teads.tv/teads-fellow.js", 
    ];
    var cookiePreferences = getCookieConsentPreferences("cp");
    return /*#__PURE__*/ _jsxs(React.Fragment, {
        children: [
            /*#__PURE__*/ _jsx(Script, {
                id: "segment-snippet",
                dangerouslySetInnerHTML: {
                    __html: segmentSnippet
                },
                strategy: "beforeInteractive"
            }, "segment-snippet"),
            /*#__PURE__*/ _jsx(Script, {
                "data-testid": "data-layer",
                id: "data-layer",
                strategy: "beforeInteractive",
                dangerouslySetInnerHTML: {
                    __html: "window.adobeData = ".concat(JSON.stringify({
                        page: {
                            pageInfo: {
                                brand: "cinch",
                                pageName: "",
                                pageURL: "",
                                previousPageName: ""
                            },
                            user: {
                                aaId: ""
                            }
                        }
                    }), ";")
                }
            }, "data-layer"),
            /*#__PURE__*/ _jsx(Script, {
                id: "digital-data-layer",
                strategy: "beforeInteractive",
                dangerouslySetInnerHTML: {
                    __html: "\n            window.digitalData = window.digitalData || []; \n            window.dataLayer = window.dataLayer || []; \n            window.gtag = function () { window.dataLayer.push(arguments) }\n          "
                }
            }, "digital-data-layer"),
            /*#__PURE__*/ _jsx(SimpleAnalyticsScripts, {}),
            (cookiePreferences === null || cookiePreferences === void 0 ? void 0 : cookiePreferences.functional) ? /*#__PURE__*/ _jsx(Script, {
                id: "AWIN",
                src: "https://www.dwin1.com/30879.js",
                type: "text/javascript"
            }) : null,
            (cookiePreferences === null || cookiePreferences === void 0 ? void 0 : cookiePreferences.analytics) && /*#__PURE__*/ _jsx(Script, {
                id: "adobe-analytics",
                src: analyticsScripts
            }, "adobe-analytics"),
            (cookiePreferences === null || cookiePreferences === void 0 ? void 0 : cookiePreferences.advertising) && marketingScripts.map(function(marketingScript, index) {
                return /*#__PURE__*/ _jsx(Script, {
                    id: "marketing-analytics-".concat(index),
                    src: marketingScript
                }, "marketing-analytics-".concat(index));
            })
        ]
    }, "analytics-manager-scripts");
};
